import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { getSelections } from "../../lib/api";
import { getDateFormat, isObjEmpty } from "../../lib";
import SelectionRow from "../SelectionRow";
import SelectionsLoadingSkeleton from "../SelectionsLoadingSkeleton";
import ShowMoreButton from "../ShowMoreButton";
import styles from "./SelectionsContainer.module.scss";
import {
  PagingLimit,
  SelectionMarketNameMap,
  SelectionsContainerClassNameMap,
} from "../../constants";
import ToolTip from "../OnboardingPopup/components/ToolTip";
import { sendAnalyticsEvent } from "../../lib/postMessage";
import { convertOdds } from "../../lib/odds";
import { OPEN } from "../../constants/analytics";

const SelectionsContainer = () => {
  const [selections, setSelections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { filters, ppConfig, onboardingStep } = useSelector(
    (state) => state.filter
  );
  const { activeView, activeFilterView, experiments } = useSelector(
    (state) => state.view
  );
  const { marketGroups } = ppConfig;
  const { stats, date, fixtures, markets, sortBy, odds } = filters;
  const hasMore = selections.length % PagingLimit === 0;
  const areAllMarketFiltersEmpty = !filters.markets.length;
  const grouped = [];
  const [activeGroupedSelections, setActiveGroupedSelections] = useState([]);
  const { bubbleUI } = experiments;

  const handleGroupSelections = (returnedSelections) => {
    returnedSelections.map((selection) => {
      let found = false;

      if (grouped.length > 0) {
        grouped.map((group) => {
          if (
            group.competitionId === selection.competitionId &&
            group.marketType === selection.marketType &&
            group.probability === selection.probability
          ) {
            found = true;

            group.selections.push(selection);
          }
        });
      }

      if (!found) {
        grouped.push({
          competitionId: selection.competitionId,
          marketType: selection.marketType,
          probability: selection.probability,
          selections: [selection],
        });
      }
    });

    return grouped;
  };

  const getBetBuilderSelections = async (reset = false) => {
    if (!hasMore && !reset) {
      setIsLoading(false);
      return;
    }

    const storedFixtures =
      date.length === 1
        ? fixtures[date[0]]
        : [...fixtures.today, ...fixtures.tomorrow];

    const numberOfMarketsAvailable = marketGroups?.reduce(
      (sum, marketGroup) => sum + marketGroup.markets.length,
      0
    );

    const numberOfFixturesAvailable = parseInt(
      localStorage.getItem("numberOfAvailableFixtures")
    );

    const handleDateParam = date.length === 1 ? getDateFormat(date[0]) : null;

    const handleMarketsParam =
      markets.length > 0 && markets.length !== numberOfMarketsAvailable
        ? markets.join()
        : null;

    const {
      minOdds,
      maxOdds,
      isDecimal = false,
    } = !isObjEmpty(odds) ? odds : { minOdds: null, maxOdds: null };

    const handleFixturesParam =
      storedFixtures.length > 0 &&
      storedFixtures.length !== numberOfFixturesAvailable
        ? storedFixtures.join()
        : null;
    const minOddsParam = convertOdds({
      odds: (isDecimal || bubbleUI) && minOdds ? minOdds.toFixed(2) : minOdds,
      type: "min",
    });
    const maxOddsParam = convertOdds({
      odds: (isDecimal || bubbleUI) && maxOdds ? maxOdds.toFixed(2) : maxOdds,
      type: "max",
    });

    const res = await getSelections({
      basedOn: stats,
      order: sortBy,
      date: handleDateParam,
      markets: handleMarketsParam,
      fixtures: handleFixturesParam,
      limit: selections.length > 0 && !reset ? PagingLimit.toString() : null,
      offset:
        selections.length > 0 && !reset ? selections.length.toString() : null,
      minOdds: minOddsParam ? minOddsParam.toString() : null,
      maxOdds: maxOddsParam ? maxOddsParam.toString() : null,
    });

    if (res.code === 0) {
      setSelections(
        reset ? res.selections : [...selections, ...res.selections]
      );
    }
    setIsLoading(false);
    return true;
  };

  const fetchSelections = useCallback(async () => {
    setIsLoading(true);
    getBetBuilderSelections(true);
  }, [stats, date, fixtures, markets, sortBy, odds]);

  const handleSlice = (groupSelectionsLength, groupSelectionIndex) => {
    // If the activeGroupedSelection is equal to the passed down groupSelectionIndex
    // is equal to true we should reveal all the remaining group selections if there are more than 3 to begin with.
    if (activeGroupedSelections.includes(groupSelectionIndex)) {
      return groupSelectionsLength;
    }
    // Otherwise we default to 3 to begin with.
    return 3;
  };

  const handleShowMoreSelections = (groupSelectionIndex, selection) => {
    sendAnalyticsEvent({
      event: "XMoreSelections",
      type: "SeeMore",
      action: OPEN,
      market: selection.marketType,
      competitionName: selection.competitionName,
      probability: selection.probability,
    });
    setActiveGroupedSelections([
      ...activeGroupedSelections,
      groupSelectionIndex,
    ]);
  };

  const renderShowMore = (
    groupedSelection,
    selection,
    index,
    groupSelectionIndex
  ) => {
    // If the activeGroupSelections array does not contain the passed down groupSelectionIndex,
    // there are more than 3 selections coming back from the grouped selections, then we should render the Show More Button
    // underneath the 3rd indexed selection.
    if (
      !activeGroupedSelections.includes(groupSelectionIndex) &&
      groupedSelection.selections.length > 3 &&
      index === 2
    ) {
      return (
        <ShowMoreButton
          numberOfSelections={groupedSelection.selections.length}
          selection={SelectionMarketNameMap.get(selection.marketType)}
          probability={selection.probability}
          competition={selection.competitionName}
          onClick={() =>
            handleShowMoreSelections(groupSelectionIndex, selection)
          }
        />
      );
    }
    return null;
  };

  const renderSelectionRows = () => {
    if (
      areAllMarketFiltersEmpty &&
      (sortBy === "stats-desc" || sortBy === "stats-asc")
    ) {
      // Display grouped selections if no filters are selected - handling the grouping in handleGroupSelections
      return handleGroupSelections(selections).map(
        (groupedSelection, groupSelectionIndex) =>
          groupedSelection.selections
            .slice(
              0,
              handleSlice(
                groupedSelection.selections.length,
                groupSelectionIndex
              )
            )
            .map((selection, index) => (
              <React.Fragment
                key={`selection-${selection.mappedEventId}-${selection.selection.id}-${selection.selection.marketId}`}
              >
                <SelectionRow {...selection} location="Results" />
                {renderShowMore(
                  groupedSelection,
                  selection,
                  index,
                  groupSelectionIndex
                )}
              </React.Fragment>
            ))
      );
    } else {
      return selections.map((selection) => (
        <SelectionRow
          key={`selection-${selection.mappedEventId}-${selection.selection.id}-${selection.selection.marketId}`}
          {...selection}
          location="Results"
        />
      ));
    }
  };

  const handleSelectionsContainerClass = () => {
    if (onboardingStep === 3 || onboardingStep === 4 || onboardingStep === 5) {
      return SelectionsContainerClassNameMap.get(onboardingStep);
    }

    return "";
  };

  useEffect(() => {
    // Only fetch the selections when the any of the filters update in the filters object and
    // if activeView is an empty string
    if (!activeView && !activeFilterView) {
      window.scrollTo(0, 0);
      fetchSelections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeView,
    activeFilterView,
    odds,
    markets,
    stats,
    date,
    fixtures,
    sortBy,
  ]);

  // This is will show the loading skeleton on first render
  if (isLoading)
    return (
      <SelectionsLoadingSkeleton
        selections={
          areAllMarketFiltersEmpty
            ? selections.map((selection) => selection).selections
            : selections
        }
      />
    );

  if (!selections?.length)
    return (
      <div className={styles.noSelections}>
        <h3>No Selections</h3>
        <p>Try changing your choices</p>
      </div>
    );

  return (
    <div
      className={`${styles.selectionsContainer} ${
        styles[`${handleSelectionsContainerClass()}`]
      }`}
    >
      {/* If onboarding is active then we show the ToolTip relative to the selectionsContainer, stats circle or the selectionContainer with form opened */}
      {onboardingStep === 3 || onboardingStep === 4 || onboardingStep === 5 ? (
        <ToolTip />
      ) : null}
      {renderSelectionRows()}
      {hasMore ? (
        <button
          type="button"
          className={styles.showMoreButton}
          onClick={() => {
            sendAnalyticsEvent({
              event: "SeeMoreSelections",
              type: "SeeMore",
              action: OPEN,
            });
            getBetBuilderSelections();
          }}
        >
          <span>See more selections</span>
        </button>
      ) : null}
    </div>
  );
};

export default SelectionsContainer;
